import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../Common/Sidebar";
import Loader from "../Common/Loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "react-bootstrap";
import CommonValues from "../Common/utils";

interface Employee {
  id: number;
  departmentId: number;
  employeeName: string;
  role: string;
  address: string;
  state: string;
  city: string;
  zip: string;
  contactNo: string;
  email: string;
  about: string;
  skills: string;
}
interface Department {
  id: number;
  departmentName: string;
}

export default function EmployeePage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [department, setDepartment] = useState<string | null>(null);
  const [role, setRole] = useState("");
  const [empname, setEmpName] = useState("");
  const [city, setCity] = useState<string | null>(null);
  const [skills, setSkills] = useState("");
  const [showWideSidebar, setShowWideSidebar] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const token = CommonValues.GetToken();
        const headers = { Authorization: `Bearer ${token}` };

        const empResponse = await axios.get(
          `${process.env.REACT_APP_Alexandria_API}/api/Alexandria/employee/get`,
          { headers }
        );
        const empData = Array.isArray(empResponse.data?.data) ? empResponse.data.data : [];
        setEmployees(empData);

        const uniqueCities = Array.from(
          new Set(empData.map((emp: Employee) => emp.city))
        ) as string[];
        setCityOptions(uniqueCities);
      } catch (err) {
        console.error("Error fetching employees", err);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchDepartments = async () => {
      try {
        const token = CommonValues.GetToken();
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(
          `${process.env.REACT_APP_Alexandria_API}/api/Alexandria/department/get`,
          { headers }
        );
        const deptData = Array.isArray(response.data?.data) ? response.data.data : [];
        setDepartments(deptData);
      } catch (err) {
        console.error("Error fetching departments", err);
        setDepartments([]);
      }
    };

    fetchEmployees();
    fetchDepartments();
  }, []);

  const getDepartmentName = (deptId: number) => {
  const dept = departments.find((d) => d.id === deptId);
  return dept ? dept.departmentName : "";
};


  const filteredData = employees.filter((emp) => {
    const matchDepartment = department ? emp.departmentId?.toString() === department : true;
    const matchRole = role ? emp.role?.toLowerCase().includes(role.toLowerCase()) : true;
    const matchEmpname = empname ? emp.employeeName?.toLowerCase().includes(empname.toLowerCase()) : true;
    const matchCity = city ? emp.city?.toLowerCase() === city.toLowerCase() : true;
    const matchSkills = skills ? emp.skills?.toLowerCase().includes(skills.toLowerCase()) : true;
    return matchDepartment && matchRole && matchEmpname && matchCity && matchSkills;
  });

  const clearFilters = () => {
    setDepartment(null);
    setRole("");
    setEmpName("");
    setCity(null);
    setSkills("");
  };

  const onSidebarValueChanged = (value: boolean) => setShowWideSidebar(value);

  return (
    <div className="d-flex vh-100 bg-light">
      <Sidebar pagename="employee" onSidebarValueChanged={onSidebarValueChanged} />
      <div className={`flex-grow-1 d-flex flex-column bg-light p-2 mt-4`}>
        <div className={`${showWideSidebar ? "main-content-large-width" : "main-content-small-width"}`}>
          <div>
            <h2 className="fw-bold">Employee Directory</h2>
            <p className="text-muted">All employees in the company</p>
          </div>
          <div className="card p-3">
            <div className="d-flex gap-4">
              <div className="info-block dashboard-card shadow-hover p-4 rounded-4 w-100">
                <h2 className="dashboard-number mb-1">{employees.length}</h2>
                <p className="dashboard-label mb-0">
                  <i className="fas fa-users me-2"></i>Total Employees
                </p>
              </div>
            </div>
            <p className="text-purple header-text mb-1 mt-4">Filter By:</p>
            <div className="row p-1 d-flex align-items-end">
              <div className="col-md">
                <label style={{ fontSize: "12px", color: "#381574" }}>Employee Name</label>
                <InputText value={empname} onChange={(e) => setEmpName(e.target.value)} className="custom-inputbox" placeholder="Filter by Employee Name" />
              </div>
              <div className="col-md">
                <label style={{ fontSize: "12px", color: "#381574" }}>Department</label>
                <Dropdown
                  value={department}
                  onChange={(e) => setDepartment(e.value)}
                  options={departments.map((d) => ({ label: d.departmentName, value: d.id.toString() }))}
                  placeholder="Filter by Department"
                  className="custom-dropdownbox"
                />
              </div>
              <div className="col-md">
                <label style={{ fontSize: "12px", color: "#381574" }}>Role</label>
                <InputText value={role} onChange={(e) => setRole(e.target.value)} className="custom-inputbox" placeholder="Filter by Employee Role" />
              </div>
              <div className="col-md">
                <label style={{ fontSize: "12px", color: "#381574" }}>Location</label>
                <Dropdown
                  value={city}
                  onChange={(e) => setCity(e.value)}
                  options={cityOptions.map((c) => ({ label: c, value: c }))}
                  placeholder="Filter by Location"
                  className="custom-dropdownbox"
                />
              </div>
              <div className="col-md-auto d-flex align-items-end">
                <Button onClick={clearFilters} size="sm" className="fa fa-undo undo-btn" title="Clear filters" />
              </div>
            </div>
            {loading ? (
             <div className="d-flex justify-content-center">
                <Loader />
            </div>
            ) : (
              <TabView className="mb-2 tabview-tab">
                <TabPanel header="List View">
                  <DataTable
                    value={filteredData}
                    className="support-call-table custom-bordered-datatable text-14"
                    emptyMessage="No employees found."
                    paginator rows={5}
                  >
                    <Column field="employeeName" header="Name"/>
                    <Column field="role" header="Role" sortable />
                    <Column field="city" header="Location" sortable />
                    <Column field="state" header="State" sortable />
                    <Column header="Department" body={(rowData) => getDepartmentName(rowData.departmentId)} />
                  </DataTable>
                </TabPanel>
                <TabPanel header="Card View">
                  <div className="card-row">
                    {filteredData.map((emp, idx) => (
                      <div key={idx} className="card-col">
                        <div className="card-container p-3" style={{ cursor: "pointer" }}>
                         <div className="small-muted-text">Employee Name</div>
                          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{emp.employeeName}</div>
                          <p className="mb-1 text-muted">{emp.role}</p>
                           <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                             <i className="pi pi-map-marker" style={{ color: "#e376f4", fontSize: "14px" }} />
                              <span className="text-muted">{emp.state}</span>
                           </div>
                           <div  className="companystyled-badge mt-2" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                             <i className="pi pi-building" style={{ color: "#381574", fontSize: "12px" }} />
                           <div>{getDepartmentName(emp.departmentId)}</div> 
                          </div>
                          <div className="mt-2">
                            <Button size="sm" style={{backgroundColor: "#3b0a69",fontSize: "12px",padding: "4px 8px",width: "100px",}}>
                              View Profile
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabPanel>
              </TabView>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
